/**
 * useCreditReminders — rappels de crédit vers l'app PWA du CLIENT.
 *
 * Le client (compte Zifek) est rattaché à un compte de crédit via
 * credit_customers.linked_user_id. Quand l'app est installée et que les
 * notifications sont autorisées, ce hook affiche des notifications locales :
 *  - « Nouvelle dette » quand une opération de crédit est ajoutée ;
 *  - « Échéance proche » quand la date de paiement habituelle approche.
 *
 * Aucune donnée n'est modifiée : le hook ne fait que lire et notifier.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import {
  notificationsSupported,
  notificationPermission,
  showLocalNotification,
} from '@/lib/pwa';

interface LinkedCustomer {
  id: number;
  full_name: string;
  usual_payment_date: string | null;
}

interface TxRow {
  id: number;
  customer_id: number;
  type: string;
  amount: number;
  description: string | null;
  created_at: string;
}

const SEEN_TX_KEY = 'zifek-credit-last-tx';
const DUE_KEY = 'zifek-credit-last-due';

function readSeen(key: string): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(key) || '');
  } catch {
    return {};
  }
}

function writeSeen(key: string, value: Record<string, string>) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* stockage indisponible */
  }
}

/** Prochaine échéance (JJ) sous N jours → renvoie le nb de jours. */
function daysUntilDue(usualPaymentDate: string | null): number | null {
  if (!usualPaymentDate) return null;
  const day = parseInt(String(usualPaymentDate).replace(/\D/g, ''), 10);
  if (Number.isNaN(day) || day < 1 || day > 31) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  let due = new Date(now.getFullYear(), now.getMonth(), day);
  if (due.getTime() < now.getTime()) {
    due = new Date(now.getFullYear(), now.getMonth() + 1, day);
  }
  return Math.round((due.getTime() - now.getTime()) / 86400000);
}

export function useCreditReminders() {
  const { user } = useAuth();
  const [hasAccount, setHasAccount] = useState(false);
  const [permission, setPermission] = useState(notificationPermission());
  const running = useRef(false);

  const tick = useCallback(async () => {
    if (!user?.id || running.current) return;
    running.current = true;
    try {
      const perm = notificationPermission();
      if (!notificationsSupported() || perm !== 'granted') return;

      const { data: customers } = await supabase
        .from('credit_customers')
        .select('id, full_name, usual_payment_date')
        .eq('linked_user_id', user.id)
        .eq('status', 'active');
      const list = (customers || []) as LinkedCustomer[];
      setHasAccount(list.length > 0);
      if (list.length === 0) return;

      const ids = list.map((c) => c.id);
      const { data: txs } = await supabase
        .from('credit_transactions')
        .select('id, customer_id, type, amount, description, created_at')
        .in('customer_id', ids)
        .order('id', { ascending: false })
        .limit(40);
      const rows = (txs || []) as TxRow[];

      // ── Nouvelle dette ──
      const seenTx = readSeen(SEEN_TX_KEY);
      const freshCredits: TxRow[] = [];
      rows.forEach((tx) => {
        const key = String(tx.customer_id);
        const last = seenTx[key] ? Number(seenTx[key]) : 0;
        if (tx.type === 'credit' && last > 0 && tx.id > last) freshCredits.push(tx);
      });
      // Mémorise l'id le plus récent par client (sans notifier à la 1re sync).
      rows.forEach((tx) => {
        const key = String(tx.customer_id);
        const last = seenTx[key] ? Number(seenTx[key]) : 0;
        if (tx.id > last) seenTx[key] = String(tx.id);
      });
      writeSeen(SEEN_TX_KEY, seenTx);

      for (const tx of freshCredits.slice(0, 3)) {
        const cust = list.find((c) => c.id === tx.customer_id);
        await showLocalNotification(
          'Nouvelle dette',
          `${cust?.full_name || 'Votre compte'} : ${Number(tx.amount).toLocaleString()}${tx.description ? ` — ${tx.description}` : ''}`,
          { tag: `zifek-credit-${tx.id}`, url: `${import.meta.env.BASE_URL || '/'}mon-compte` },
        );
      }

      // ── Échéance proche (≤ 3 jours) ──
      const seenDue = readSeen(DUE_KEY);
      for (const cust of list) {
        const days = daysUntilDue(cust.usual_payment_date);
        if (days == null || days > 3) continue;
        const stamp = `${new Date().getFullYear()}-${new Date().getMonth()}`;
        if (seenDue[String(cust.id)] === stamp) continue;
        seenDue[String(cust.id)] = stamp;
        await showLocalNotification(
          'Échéance proche',
          `Le paiement de ${cust.full_name} arrive à échéance${days <= 0 ? " aujourd'hui" : ` dans ${days} jour(s)`}.`,
          { tag: `zifek-due-${cust.id}`, url: `${import.meta.env.BASE_URL || '/'}mon-compte` },
        );
      }
      writeSeen(DUE_KEY, seenDue);
    } catch {
      /* silencieux : un échec réseau ne doit pas casser l'app */
    } finally {
      running.current = false;
    }
  }, [user]);

  useEffect(() => {
    tick();
    const interval = setInterval(tick, 60000);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [tick]);

  const enable = useCallback(async () => {
    if (!notificationsSupported()) return;
    try {
      const result = await Notification.requestPermission();
      setPermission(result as typeof permission);
      if (result === 'granted') tick();
    } catch {
      /* refus ou indisponible */
    }
  }, [tick]);

  return { hasAccount, permission, enable };
}

export default useCreditReminders;